import {inject, Injectable} from '@angular/core';
import {Capacitor} from '@capacitor/core';
import {Preferences} from '@capacitor/preferences';
import {StatusBar, Style} from '@capacitor/status-bar';
import {BehaviorSubject, distinctUntilChanged, Observable} from 'rxjs';
import {LoggerService} from '../logging/logger.service';

/** What the user chose on the Settings screen. */
export type AppearancePreference = 'light' | 'dark' | 'system';

/** The scheme actually painted once `system` is resolved. */
export type ColourScheme = 'light' | 'dark';

/**
 * What sits under the native status bar: brand chrome (white
 * text) or the light scheme's ground (ink text).
 */
export type StatusBarSurface = 'brand' | 'ground';

/** The @capacitor/preferences key the choice is stored under. */
export const APPEARANCE_KEY = 'hec_appearance';

/** Ionic's palette hook, selecting the dark token set. */
const DARK_CLASS = 'ion-palette-dark';

const PREFERENCES: readonly AppearancePreference[] =
  ['light', 'dark', 'system'];

function isPreference(
  value: string | null
): value is AppearancePreference {
  return PREFERENCES.includes(value as AppearancePreference);
}

/**
 * Owns the light/dark choice (HEC-323): reads it from device
 * storage, resolves `system` against the OS setting, toggles
 * Ionic's `ion-palette-dark` class on the document root and keeps
 * the native status bar text readable over whatever surface sits
 * under it. Runs from a provideAppInitializer in main.ts so the
 * class is right before the root component first paints.
 *
 * Deliberately not PreferenceService: that keys every value on
 * the signed-in user and refuses to save with none, and the
 * scheme must apply before login too.
 */
@Injectable({providedIn: 'root'})
export class ThemeService {
  private readonly logger = inject(LoggerService);

  /** The stored choice; `system` until initialise() has read it. */
  readonly preference$ =
    new BehaviorSubject<AppearancePreference>('system');

  private readonly darkQuery: MediaQueryList =
    window.matchMedia('(prefers-color-scheme: dark)');

  private readonly schemeSubject =
    new BehaviorSubject<ColourScheme>(this.resolve());

  /** The effective scheme, emitting only when it changes. */
  readonly scheme$: Observable<ColourScheme> =
    this.schemeSubject.pipe(distinctUntilChanged());

  private surface: StatusBarSurface = 'brand';
  private listening = false;
  private readonly onSystemChange = (): void => this.apply();

  get scheme(): ColourScheme {
    return this.schemeSubject.value;
  }

  async initialise(): Promise<void> {
    const {value} = await Preferences.get({key: APPEARANCE_KEY});
    this.preference$.next(isPreference(value) ? value : 'system');
    this.apply();
  }

  async setPreference(preference: AppearancePreference): Promise<void> {
    this.preference$.next(preference);
    this.apply();
    await Preferences.set({key: APPEARANCE_KEY, value: preference});
  }

  /**
   * Screens whose top edge is the light scheme's ground (the
   * activity form, the learner and category sheets) say so while
   * they are showing; everything else is brand chrome.
   */
  setStatusBarSurface(surface: StatusBarSurface): void {
    this.surface = surface;
    this.applyStatusBar(this.schemeSubject.value);
  }

  private resolve(): ColourScheme {
    const preference = this.preference$.value;
    if (preference === 'system') {
      return this.darkQuery.matches ? 'dark' : 'light';
    }
    return preference;
  }

  private apply(): void {
    const scheme = this.resolve();
    const root = document.documentElement;
    root.classList.toggle(DARK_CLASS, scheme === 'dark');
    // Native controls and overlays follow the painted scheme in
    // light; in dark the inline value goes so they behave as
    // they did before this switch existed.
    if (scheme === 'light') {
      root.style.colorScheme = 'light';
    } else {
      root.style.removeProperty('color-scheme');
    }
    this.listenToSystem(this.preference$.value === 'system');
    this.schemeSubject.next(scheme);
    this.applyStatusBar(scheme);
  }

  private listenToSystem(on: boolean): void {
    if (on === this.listening) {
      return;
    }
    if (on) {
      this.darkQuery.addEventListener('change', this.onSystemChange);
    } else {
      this.darkQuery.removeEventListener('change', this.onSystemChange);
    }
    this.listening = on;
  }

  /**
   * Style.Dark is white text (over brand chrome or the dark
   * scheme); Style.Light is ink text, only for a ground surface
   * in the light scheme. Web builds have no status bar.
   */
  private applyStatusBar(scheme: ColourScheme): void {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    const style = this.surface === 'ground' && scheme === 'light'
      ? Style.Light
      : Style.Dark;
    StatusBar.setStyle({style}).catch(error =>
      this.logger.warn('ThemeService: status bar style failed', error));
  }
}
